"use client";

import { useEffect, useRef } from "react";

import { observer } from "mobx-react-lite";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { disposeApi } from "@/api";
import { useStore } from "@/store";
import { docPageUrl, teamPageUrl } from "@/urls";
import { findFirstDocument, getFirstDocumentOfTeam } from "@/utils/getFirstDocumentOfTeam";
import { trackEvent } from "@/utils/trackEvent";
import { getLastDocIdForTeam } from "@/utils/useLastDocId";

import type { TeamModel } from "@/models/TeamModel";

type Props = { teamPermalink: string; preview?: boolean };

// The personal team's Getting Started landing is reported by `/getting-started` instead.
function trackGettingStartedPageViewed(team: TeamModel, docId: string) {
  if (team.kind === "personal") return;

  trackEvent("getting_started_page_viewed", {
    doc_id: docId,
    team_uid: team.uid,
    team_kind: team.kind,
  });
}

export const TeamRoot = observer(function TeamRootPage({ teamPermalink, preview }: Props) {
  const { publicProfile, auth, teams } = useStore();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSignupLanding = searchParams.get("signup") === "true";
  const queryString = searchParams.toString();
  const fullUrl = `${pathname}${queryString ? `?${queryString}` : ""}`;
  const publicProfileTeamPermalink = (publicProfile.team?.permalink || "").toLowerCase();
  const hasTrackedGettingStarted = useRef(false);

  useEffect(() => {
    if (auth.status !== "ready") {
      return;
    }

    return disposeApi(async (signal) => {
      await publicProfile.fetchTeam(teamPermalink, signal);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  useEffect(() => {
    if (publicProfileTeamPermalink !== teamPermalink) {
      return;
    }

    let targetDocId = findFirstDocument(publicProfile.published?.files)?.uid;
    if (publicProfile.isOurProfile) {
      const matchedTeam = teams.userTeams.find(
        (team) => team.permalink.toLowerCase() === teamPermalink,
      );
      if (matchedTeam) {
        teams.setCurrentTeam(matchedTeam);
      }
      const lastDocId = isSignupLanding ? "" : getLastDocIdForTeam(matchedTeam?.uid || "");
      if (!preview) {
        const { team } = publicProfile;
        targetDocId = lastDocId || getFirstDocumentOfTeam(team)?.uid;
        if (isSignupLanding && team && targetDocId && !hasTrackedGettingStarted.current) {
          hasTrackedGettingStarted.current = true;
          trackGettingStartedPageViewed(team, targetDocId);
        }
      }
    }

    const targetUrl = targetDocId
      ? docPageUrl({ docId: targetDocId, preview })
      : teamPageUrl({ teamPermalink, preview });

    if (targetUrl !== fullUrl) {
      router.replace(targetUrl);
    }
  }, [
    publicProfileTeamPermalink,
    publicProfile.team,
    publicProfile.published,
    publicProfile.isOurProfile,
    preview,
    isSignupLanding,
    router,
    teamPermalink,
    teams,
    fullUrl,
  ]);

  return null;
});
